import React from 'react';

import { Checkbox, MenuItem } from '@mui/material';

import { StyledAutocomplete, StyledSelect, StyledTextField } from 'components/Input/styles';
import { renderLimitedChips } from 'components/Input/limitChips';

import CustomUserDropdownField from 'components/SharedFormComponents/CustomUserDropdownField/CustomUserDropdownField';
import UserAndGroupPicker, {
  isPickerUser,
  PickerItemKind,
  PickerUser,
} from 'components/SharedFormComponents/UserAndGroupPicker';
import EntitySearchField from 'components/EntitySearchField/EntitySearchField';

import { COUNTRIES, Country } from 'components/CountrySelect/countries';

import { EntityModules } from 'core/constants';

import { RuleOperator } from 'utils/api/modules/memberGroups';
import { UuidableName } from 'utils/api/types';
import { UserFieldOption } from 'utils/api/modules/user/user.types';

import { attributeKind, isMultiValueOperator, isNoValueOperator, optionKindSlug, RuleValue } from './registry';
import { NoValuePlaceholder, ValueCell } from './styles';

export interface RuleValueControlProps {
  attribute: string;
  operator: RuleOperator;
  values: RuleValue[];
  onChange: (values: RuleValue[]) => void;
  optionsByKind: Record<string, UserFieldOption[]>;
  error?: boolean;
}

const asUuidableName = (value: RuleValue): UuidableName =>
  typeof value === 'string' ? { uuid: value, name: value } : value;

// One chip plus "+N" keeps the value box level with the attribute and operator boxes.
const MAX_CHIPS = 1;

const RuleValueControl: React.FunctionComponent<RuleValueControlProps> = ({
  attribute,
  operator,
  values,
  onChange,
  optionsByKind,
  error,
}) => {
  if (isNoValueOperator(operator)) {
    return <NoValuePlaceholder aria-hidden />;
  }

  const kind = attributeKind(attribute);
  const multiple = isMultiValueOperator(operator);

  if (kind === 'text') {
    const current = typeof values[0] === 'string' ? values[0] : '';
    return (
      <ValueCell data-testid="rule-condition-value">
        <StyledTextField
          fullWidth
          value={current}
          placeholder="Enter a value"
          error={error}
          inputProps={{ 'aria-label': 'Value' }}
          onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
        />
      </ValueCell>
    );
  }

  if (kind === 'country') {
    const selected = values
      .map((value) => COUNTRIES.find((c) => c.code === (typeof value === 'string' ? value : value.uuid)) ?? null)
      .filter((c): c is Country => c !== null);
    if (multiple) {
      return (
        <ValueCell data-testid="rule-condition-value">
          <StyledAutocomplete
            multiple
            options={COUNTRIES}
            renderValue={renderLimitedChips((option: Country) => option.name, MAX_CHIPS)}
            value={selected}
            disableCloseOnSelect
            getOptionLabel={(option: Country) => option.name}
            isOptionEqualToValue={(a: Country, b: Country) => a.code === b.code}
            onChange={(_, options) => onChange((options as Country[]).map((c) => c.code))}
            renderInput={(params) => (
              <StyledTextField {...params} error={error} placeholder={selected.length ? '' : 'Select countries'} />
            )}
          />
        </ValueCell>
      );
    }
    return (
      <ValueCell data-testid="rule-condition-value">
        <StyledAutocomplete
          options={COUNTRIES}
          value={selected[0] ?? null}
          getOptionLabel={(option: Country) => option.name}
          isOptionEqualToValue={(a: Country, b: Country) => a.code === b.code}
          onChange={(_, option) => onChange(option ? [(option as Country).code] : [])}
          renderInput={(params) => <StyledTextField {...params} error={error} placeholder="Select country" />}
        />
      </ValueCell>
    );
  }

  if (kind === 'site') {
    return (
      <ValueCell data-testid="rule-condition-value">
        <EntitySearchField
          key={multiple ? 'multiple' : 'single'}
          field="site"
          module={EntityModules.Sites}
          multiple={multiple}
          maxChips={MAX_CHIPS}
          error={error}
          data={multiple ? values.map(asUuidableName) : values[0] ? asUuidableName(values[0]) : null}
          updateField={(_, value) => {
            if (value === null) {
              onChange([]);
            } else if (Array.isArray(value)) {
              onChange(value);
            } else {
              onChange([value]);
            }
          }}
        />
      </ValueCell>
    );
  }

  if (kind === 'manager') {
    if (multiple) {
      const managerValues: PickerUser[] = values
        .map(asUuidableName)
        .map(({ uuid, name }) => ({ kind: PickerItemKind.User, uuid, name }));
      return (
        <ValueCell data-testid="rule-condition-value">
          <UserAndGroupPicker
            value={managerValues}
            onChange={(next) => onChange(next.filter(isPickerUser).map(({ uuid, name }) => ({ uuid, name })))}
            placeholder="Select line managers"
            maxChips={MAX_CHIPS}
            error={error}
          />
        </ValueCell>
      );
    }
    return (
      <ValueCell data-testid="rule-condition-value">
        <CustomUserDropdownField
          value={values[0] ? asUuidableName(values[0]) : null}
          error={error}
          description=""
          onChange={(user) => onChange(user ? [{ uuid: user.uuid, name: user.name }] : [])}
        />
      </ValueCell>
    );
  }

  const slug = optionKindSlug(attribute);
  const options = (slug && optionsByKind[slug]) || [];
  const optionUuids = new Set(options.map((o) => o.uuid));
  const labelFor = (value: RuleValue): UuidableName => {
    const uuid = typeof value === 'string' ? value : value.uuid;
    const option = options.find((o) => o.uuid === uuid);
    return option ? { uuid: option.uuid, name: option.name } : asUuidableName(value);
  };

  if (multiple) {
    const selected: UuidableName[] = values.map(labelFor);
    const optionObjects: UuidableName[] = [
      ...options.map((o) => ({ uuid: o.uuid, name: o.name })),
      ...selected.filter((s) => !optionUuids.has(s.uuid)),
    ];
    return (
      <ValueCell data-testid="rule-condition-value">
        <StyledAutocomplete
          multiple
          disableCloseOnSelect
          options={optionObjects}
          renderValue={renderLimitedChips((option: UuidableName) => option.name, MAX_CHIPS)}
          value={selected}
          getOptionLabel={(option: UuidableName) => option.name}
          isOptionEqualToValue={(a: UuidableName, b: UuidableName) => a.uuid === b.uuid}
          renderOption={(props, option: UuidableName, { selected: isSelected }) => (
            <li {...props} key={option.uuid}>
              <Checkbox checked={isSelected} />
              {option.name}
            </li>
          )}
          onChange={(_, chosen) => onChange(chosen as UuidableName[])}
          renderInput={(params) => (
            <StyledTextField {...params} error={error} placeholder={selected.length ? '' : 'Select values'} />
          )}
        />
      </ValueCell>
    );
  }

  const current = values[0] ? (typeof values[0] === 'string' ? values[0] : values[0].uuid) : '';
  return (
    <ValueCell data-testid="rule-condition-value">
      <StyledSelect
        fullWidth
        displayEmpty
        error={error}
        value={current}
        inputProps={{ 'aria-label': 'Value' }}
        onChange={(event) => {
          const uuid = event.target.value as string;
          onChange(uuid ? [labelFor(uuid)] : []);
        }}
      >
        <MenuItem value="" disabled>
          Select a value
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={option.uuid} value={option.uuid}>
            {option.name}
          </MenuItem>
        ))}
      </StyledSelect>
    </ValueCell>
  );
};

export default RuleValueControl;
